import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { getNotFoundSEO, validateSEOData, generateKeywords } from "@/services/seoService";

const NotFound = () => {
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Generate SEO data for NotFound page
  const seoData = validateSEOData(getNotFoundSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={generateKeywords(seoData.keywords)} />
        )}
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags - Important for 404 pages */}
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={seoData.url} />
        <meta http-equiv="status" content="404" />
      </Helmet>
      
      <div 
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
      <main className="flex-grow bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <h1 className="text-6xl font-bold text-gray-900">404</h1>
            <p className="mt-4 text-xl text-gray-600">Oops! Page not found</p>
            <p className="mt-2 text-gray-500">
              The page you're looking for doesn't exist or has been moved.
            </p>
            <div className="mt-8">
              <Button className="bg-jamaica-green text-white hover:bg-jamaica-green/90">
                <Link to="/">Return to Home</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
    </>
  );
};

export default NotFound;
